import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Avatar,
  Box,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useListPsiOpportunityInterests } from '@/lib/api/opportunity-interests.ts';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { calculateRemainingSelectedTime } from '@/features/opportunities/utils.ts';

interface ViewSelectedInvestorModalProps {
  open: boolean;
  onClose: () => void;
  opportunityId: string;
}

const ViewSelectedInvestorModal = ({
  open,
  onClose,
  opportunityId,
}: ViewSelectedInvestorModalProps) => {
  const { data: interests, isLoading } = useListPsiOpportunityInterests(opportunityId);

  // Find the selected opportunity interest
  const selectedInterest = interests?.find((interest) => interest.status === 'SELECTED');
  const investor = selectedInterest?.investor;

  if (!selectedInterest || !investor) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Ver inversor seleccionado</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary" align="center" py={2}>
              No hay ningún inversor seleccionado para esta oportunidad.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const timeData = calculateRemainingSelectedTime(selectedInterest.updatedAt);
  const remainingTime = timeData ? timeData.text : 'Expirado';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Inversor seleccionado</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} py={2}>
          {/* Investor basic info */}
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar src={investor.photoUrl} alt={investor.name} sx={{ width: 64, height: 64 }}>
              {investor.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6">{investor.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {investor.email}
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Confirmation notice */}
          <Alert severity="info" icon={<AccessTimeIcon />}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Pendiente de confirmación del inversor
            </Typography>
            <Typography variant="body2" color="text.secondary">
              El inversor debe confirmar esta oportunidad para completar la reserva.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>Tiempo restante:</strong> {remainingTime}
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewSelectedInvestorModal;
